import React, { useState } from "react";
import "./EmployeeTrainingIdentified.css";
import { empTrainingIdentifiedAPI } from "./api";
import DataTable from "./DataTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function EmployeeTrainingIdentified() {
  const [empCodeFrom, setEmpCodeFrom] = useState("");
  const [empCodeTo, setEmpCodeTo] = useState("");
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [drillRow, setDrillRow] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const handleEmpClick = async (row) => {
    setDrillRow(row);
    setDrillData([]);
    setDrillLoading(true);
    try {
      const data = await empTrainingIdentifiedAPI.getDetail(row.sal_code);
      setDrillData(Array.isArray(data) ? data : []);
    } catch {
      setDrillData([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const drillColumns = [
    { header: "Sr. No.", renderCell: (_v, _row, idx) => idx + 1 },
    { header: "Course Name", accessor: (row) => row.trng_course_name ?? "" },
    { header: "Functional Area Name", accessor: (row) => row.func_sub_area_name ?? "" },
    { header: "Confirmed", accessor: (row) => row.nomin_confirm ?? "N" },
    { header: "Attended", accessor: (row) => row.attended_yn ?? "N" },
  ];

  const columns = [
    { header: "Sr. No.", renderCell: (_v, _row, idx) => idx + 1 },
    {
      header: "Emp Name",
      renderCell: (_v, row) => (
        <span>
          {row.emp_name ?? ""}{" "}
          <span
            style={{ color: "#810055", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleEmpClick(row)}
          >
            [{row.sal_code ?? ""}]
          </span>
        </span>
      ),
    },
    { header: "TrngNeedsIdent", accessor: (row) => row.TrngNeedsIdentified_count ?? 0 },
    { header: "TrngNeedsMet", accessor: (row) => row.TrngNeedsMet_count ?? 0 },
    { header: "YetToMet", accessor: (row) => row.YetToBeMet_count ?? 0 },
  ];

  const handleView = async () => {
    setLoading(true);
    setErrorMsg(null);
    setShowTable(false);
    try {
      const params = {};
      if (empCodeFrom.trim()) params.sal_code_from = empCodeFrom.trim();
      if (empCodeTo.trim()) params.sal_code_to = empCodeTo.trim();
      const data = await empTrainingIdentifiedAPI.getAll(params);
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setErrorMsg("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmpCodeFrom("");
    setEmpCodeTo("");
    setTableData([]);
    setShowTable(false);
    setErrorMsg(null);
  };

  const handleExportExcel = () => {
    const today = new Date().toLocaleDateString("en-GB");
    const headers = ["Sr. No.", "Emp Name", "Sal Code", "TrngNeedsIdent", "TrngNeedsMet", "YetToMet"];
    const data = tableData.map((row, idx) => [
      idx + 1,
      row.emp_name ?? "",
      row.sal_code ?? "",
      row.TrngNeedsIdentified_count ?? 0,
      row.TrngNeedsMet_count ?? 0,
      row.YetToBeMet_count ?? 0,
    ]);
    const aoa = [
      ["Report Title : Employee wise Training Identified"],
      [`Report Date : ${today}`],
      [],
      headers,
      ...data,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EmployeeTrainingIdentified");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "Employee_Training_Identified.xlsx");
  };

  return (
    <div className="bd-container">

      {/* FILTERS */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
        <table style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5 }}>
          <tbody>
            <tr>
              <td className="bd-label" style={{ paddingRight: 6 }}>Emp Code From</td>
              <td style={{ paddingRight: 40 }}>
                <input className="bd-input" style={{ width: 160 }} type="text" value={empCodeFrom} onChange={(e) => setEmpCodeFrom(e.target.value)} />
              </td>
              <td className="bd-label" style={{ paddingRight: 6 }}>Emp Code To</td>
              <td>
                <input className="bd-input" style={{ width: 160 }} type="text" value={empCodeTo} onChange={(e) => setEmpCodeTo(e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ACTION ROW */}
      <div className="action-row">
        <button className="btn btn-outline" onClick={handleReset}>Reset</button>
        <button className="btn btn-outline" onClick={handleView} disabled={loading}>
          {loading ? "Loading…" : "View"}
        </button>
      </div>

      {errorMsg && <p className="bd-error">{errorMsg}</p>}

      {/* TABLE AREA */}
      {showTable && (
        <div className="bd-details">
          <p className="bd-section-title">Employee Training Identified</p>
          <DataTable
            columns={columns}
            data={tableData}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No data to display"
            rowKey={(_row, idx) => idx}
          />
          {tableData.length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="btn btn-outline" onClick={handleExportExcel}>Export to Excel</button>
            </div>
          )}
        </div>
      )}

      {/* DRILL-DOWN MODAL */}
      {drillRow && (
        <div className="facviewer-overlay" onClick={() => setDrillRow(null)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setDrillRow(null)}>&times;</button>
            <p className="bd-section-title" style={{ marginBottom: 10 }}>
              Employee Name :- {drillRow.emp_name} ({drillRow.sal_code})
            </p>
            {drillLoading ? (
              <p style={{ textAlign: "center", fontSize: 12.5 }}>Loading…</p>
            ) : (
              <DataTable
                columns={drillColumns}
                data={drillData}
                showEditCols={false}
                editRows={{}}
                onToggleEditRow={() => {}}
                onChangeEditCell={() => {}}
                selectedForDelete={{}}
                onToggleDeleteRow={() => {}}
                emptyMessage="No training records found"
                rowKey={(_row, idx) => idx}
              />
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default EmployeeTrainingIdentified;